import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { useConfirm } from "@/components/ConfirmDialog";
import { Plus, Send, Trash2, Wrench, MessageSquare } from "lucide-react";
import { toast } from "sonner";

function Bubble({ m }) {
  const me = m.role === "user";
  return (
    <div className={`flex ${me ? "justify-end" : "justify-start"}`} data-testid={`chat-msg-${m.role}`}>
      <div className={`max-w-[85%] rounded-lg px-3.5 py-2.5 text-sm leading-relaxed whitespace-pre-wrap break-words ${me ? "bg-white text-black" : m.error ? "panel border-red-500/40" : "panel"}`}>
        {(m.tools || []).length > 0 && (
          <div className="flex flex-wrap gap-1 mb-2">{m.tools.map((t, i) => (
            <span key={i} className={`pill text-[10px] ${t.status >= 400 ? "text-[var(--breach)]" : "text-neutral-400"}`} title={JSON.stringify(t.input)}><Wrench size={9} />{t.name}</span>))}</div>
        )}
        {String(m.text || "").split(/(\*\*[^*]+\*\*|`[^`]+`)/g).map((p, i) => (p.startsWith("**") && p.endsWith("**") ? <strong key={i}>{p.slice(2, -2)}</strong>
          : p.startsWith("`") && p.endsWith("`") && p.length > 1 ? <code key={i} className="kbd">{p.slice(1, -1)}</code> : p))}
      </div>
    </div>
  );
}

function ChatList({ rows, active, onPick, onNew, onDelete }) {
  return (
    <div className="panel p-2 space-y-1 h-fit md:max-h-[70vh] overflow-y-auto" data-testid="chat-list">
      <button className="btn w-full justify-center text-xs mb-1" onClick={onNew} data-testid="chat-new-btn"><Plus size={12} />New chat</button>
      {rows.map((c) => (
        <div key={c._id} className={`group flex items-center gap-1 rounded px-2 py-1.5 text-xs cursor-pointer ${active === c._id ? "bg-[var(--panel-2)] text-white" : "text-neutral-400 hover:text-white"}`}
          onClick={() => onPick(c._id)} data-testid={`chat-item-${c._id}`}>
          <MessageSquare size={11} className="shrink-0" /><span className="flex-1 truncate">{c.title}</span>
          <button className="opacity-0 group-hover:opacity-100 p-0.5 hover:text-[var(--breach)]" onClick={(e) => { e.stopPropagation(); onDelete(c); }} data-testid={`chat-delete-${c._id}`}><Trash2 size={11} /></button>
        </div>
      ))}
      {!rows.length && <div className="text-[11px] text-neutral-600 px-2">No chats yet</div>}
    </div>
  );
}

export default function ClaudeChat() {
  const { confirm } = useConfirm();
  const [rows, setRows] = useState([]);
  const [active, setActive] = useState(null);
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const end = useRef(null);
  const skipLoad = useRef(false);
  const load = () => api.get("/claude/chats").then((r) => setRows(r.data.rows));
  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (skipLoad.current) { skipLoad.current = false; return; }
    if (active) api.get(`/claude/chats/${active}`).then((r) => setMsgs(r.data.display)); else setMsgs([]);
  }, [active]);
  useEffect(() => { end.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs, busy]);

  const send = async () => {
    const t = text.trim();
    if (!t || busy) return;
    let cid = active;
    if (!cid) { const { data } = await api.post("/claude/chats"); cid = data._id; skipLoad.current = true; setActive(cid); }
    setText(""); setBusy(true);
    setMsgs((m) => [...m, { role: "user", text: t }]);
    try {
      const { data } = await api.post(`/claude/chats/${cid}/messages`, { text: t }, { timeout: 300000 });
      setMsgs((m) => [...m, data.reply]); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Claude didn't reply"); }
    finally { setBusy(false); }
  };
  const del = async (c) => {
    if (!(await confirm({ title: `Delete "${c.title}"?`, danger: true }))) return;
    await api.delete(`/claude/chats/${c._id}`); if (active === c._id) setActive(null); load();
  };
  return (
    <div className="grid grid-cols-1 md:grid-cols-[220px_minmax(0,1fr)] gap-4" data-testid="claude-chat">
      <ChatList rows={rows} active={active} onPick={setActive} onNew={() => { setActive(null); setMsgs([]); }} onDelete={del} />
      <div className="panel flex flex-col h-[70vh]">
        <div className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="chat-messages">
          {!msgs.length && <div className="text-sm text-neutral-500 space-y-1 pt-8 text-center">
            <div>Ask about your pipeline or tell Claude what to change.</div>
            <div className="text-xs text-neutral-600">e.g. "Which leads have overdue chases?" · "Log No reply on Rahul's chase" · "Add a WhatsApp step on day 7 to Type A"</div>
          </div>}
          {msgs.map((m, i) => <Bubble key={i} m={m} />)}
          {busy && <div className="text-xs text-neutral-500 animate-pulse" data-testid="chat-thinking">Claude is working…</div>}
          <div ref={end} />
        </div>
        <div className="border-t border-[var(--border)] p-3 flex gap-2">
          <textarea rows={2} className="flex-1 px-3 py-2 rounded border text-sm resize-none" placeholder="Message Claude…" value={text}
            onChange={(e) => setText(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }} data-testid="chat-input" />
          <button className="btn btn-primary self-end" onClick={send} disabled={busy || !text.trim()} data-testid="chat-send-btn"><Send size={14} /></button>
        </div>
      </div>
    </div>
  );
}
